import React, { useState, useEffect } from 'react';
import { Card, Timeline, Tag, Button, Table, Progress, Spin, Alert, Typography, Space, Badge } from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { ProcessFlowService } from '../../../services/api/processFlowService';
import type {
  ProcessFlow as ProcessFlowType,
  ProcessFlowStep,
  ProcessFlowExecution,
  ProcessFlowPerformance,
} from '../../../types/processFlow';

const { Text, Title } = Typography;

/**
 * ProcessFlowCard component for displaying process flow information in chat cards
 */
export const ProcessFlowCard: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction,
}) => {
  const [flow, setFlow] = useState<ProcessFlowType | null>(null);
  const [steps, setSteps] = useState<ProcessFlowStep[]>([]);
  const [executions, setExecutions] = useState<ProcessFlowExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract flow ID from card data - handle different card data types
  const flowId = (card.data as any)?.flow_id || (card.data as any)?.id || (card.data as any)?.flow?.id;

  useEffect(() => {
    if (flowId) {
      void loadFlowData();
    }
  }, [flowId]);

  const loadFlowData = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      setError(null);

      const [flowData, stepsData, executionsData] = await Promise.all([
        ProcessFlowService.getFlowById(flowId),
        ProcessFlowService.getFlowSteps(flowId),
        ProcessFlowService.getFlowExecutions(flowId),
      ]);

      if (flowData) {
        setFlow(flowData);
      }
      setSteps(stepsData);
      setExecutions(executionsData);
    } catch (err: any) {
      console.error('Error loading process flow data:', err);
      setError(err.message || 'Failed to load process flow');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      await ProcessFlowService.executeFlow(flowId);
      await loadFlowData();
      if (onAction) {
        onAction('flow_executed', { flow_id: flowId });
      }
    } catch (err: any) {
      console.error('Error executing flow:', err);
      setError(err.message || 'Failed to execute flow');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !flow) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <Alert
        message="Error Loading Flow"
        description={error}
        type="error"
        showIcon
        style={{ margin: '16px' }}
      />
    );
  }

  if (!flow) {
    return (
      <Alert
        message="No Flow Data"
        description="Process flow information is not available."
        type="info"
        style={{ margin: '16px' }}
      />
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'paused':
        return 'orange';
      case 'archived':
        return 'default';
      default:
        return 'blue';
    }
  };

  const getExecutionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'running':
        return 'blue';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'default';
      default:
        return 'orange';
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Flow Header */}
        <div>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={5} style={{ margin: 0 }}>
              {flow.name}
            </Title>
            <Tag color={getStatusColor(flow.status)}>{flow.status}</Tag>
          </Space>
          {flow.description && (
            <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
              {flow.description}
            </Text>
          )}
        </div>

        {/* Flow Steps */}
        {steps.length > 0 && (
          <Card size="small" title="Flow Steps">
            <Timeline
              items={steps
                .sort((a, b) => a.step_order - b.step_order)
                .map((step) => ({
                  color: step.is_required ? 'blue' : 'gray',
                  children: (
                    <div>
                      <Text strong>{step.step_name}</Text>
                      {step.description && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {step.description}
                          </Text>
                        </div>
                      )}
                      {step.estimated_duration && (
                        <Tag style={{ marginTop: '4px' }}>
                          ~{step.estimated_duration}min
                        </Tag>
                      )}
                    </div>
                  ),
                }))}
            />
          </Card>
        )}

        {/* Recent Executions */}
        {executions.length > 0 && (
          <Card size="small" title="Recent Executions">
            <Table
              dataSource={executions}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => (
                    <Badge
                      status={getExecutionStatusColor(status) as any}
                      text={status}
                    />
                  ),
                },
                {
                  title: 'Started',
                  dataIndex: 'started_at',
                  key: 'started_at',
                  render: (date: string) =>
                    date ? new Date(date).toLocaleString() : '-',
                },
                {
                  title: 'Completed',
                  dataIndex: 'completed_at',
                  key: 'completed_at',
                  render: (date: string) =>
                    date ? new Date(date).toLocaleString() : '-',
                },
              ]}
            />
          </Card>
        )}

        {/* Actions */}
        <Space>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleExecute}
            loading={loading}
          >
            Execute Flow
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadFlowData}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Space>
    </div>
  );
};

/**
 * ProcessFlowExecutionStatus component for displaying execution status
 */
export const ProcessFlowExecutionStatus: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction,
}) => {
  const [execution, setExecution] = useState<ProcessFlowExecution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract execution and flow IDs from card data
  const executionId = (card.data as any)?.execution_id;
  const flowId = (card.data as any)?.flow_id || (card.data as any)?.flow?.id;

  useEffect(() => {
    if (executionId && flowId) {
      void loadExecution();
    }
  }, [executionId, flowId]);

  const loadExecution = async () => {
    if (!executionId || !flowId) return;

    try {
      setLoading(true);
      const executions = await ProcessFlowService.getFlowExecutions(flowId);
      const exec = executions.find((e) => e.id === executionId);
      if (exec) {
        setExecution(exec);
      }
    } catch (err: any) {
      console.error('Error loading execution:', err);
      setError(err.message || 'Failed to load execution');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '16px' }}
      />
    );
  }

  if (!execution) {
    return (
      <Alert
        message="Execution Not Found"
        type="info"
        style={{ margin: '16px' }}
      />
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'running':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Space>
            {getStatusIcon(execution.status)}
            <Text strong>{execution.status.toUpperCase()}</Text>
          </Space>
        </div>

        {execution.started_at && (
          <div>
            <Text type="secondary">Started: </Text>
            <Text>{new Date(execution.started_at).toLocaleString()}</Text>
          </div>
        )}

        {execution.completed_at && (
          <div>
            <Text type="secondary">Completed: </Text>
            <Text>{new Date(execution.completed_at).toLocaleString()}</Text>
          </div>
        )}

        {execution.error_message && (
          <Alert
            message="Error"
            description={execution.error_message}
            type="error"
            showIcon
          />
        )}

        {execution.status === 'running' && (
          <Progress percent={50} status="active" />
        )}
      </Space>
    </div>
  );
};

